import { createSelector } from '@reduxjs/toolkit';
import { LOCKER_LOG_DAYS, LOCKER_LOG_SCOPE } from 'library/helpers/lockerHelpers';
import { selectLockersDevices, selectLockersDevicesReady } from 'reduxStore/devices/selectors';
import { selectLogsWindow, selectLogsWindowReady } from './windowSelectors';

// Kept out of ./windowSelectors, a leaf over state.AccessLogs alone: this one reaches
// into Devices, and that leaf is what keeps people/selectors clear of a cycle.

// Only rows the server attributed to a person reach any locker surface (requested
// 2026-09-29). One switch, not a filter per table: the tables and the usage/utilisation
// figures read the same selector, and they must not disagree about what counted.
//
// Keypad and card reads carry no PersonID — the server names those through `subject` /
// the credential holder — so this drops most of the feed on the orgs that use lockers.
// Measured on prod, last 90 days: org 412 4,547 rows → 18 (149 lockers with history → 6);
// org 629 3,731 → 1,135 (252 → 74). That is the intended behaviour, not a regression:
// set this to false to restore the full feed.
const PERSON_ATTRIBUTED_ONLY = true;

//📌 The locker slice of the shared window. /logs/accessLogs is org-wide with no category
// filter, so without the deviceID test below a door swipe would count as a locker opening.
export const selectLockerLogs = createSelector(
	[(state) => selectLogsWindow(state, LOCKER_LOG_SCOPE, LOCKER_LOG_DAYS), selectLockersDevices],
	(orgLogs, lockerDevices) => {
		const lockerDeviceIDs = new Set(lockerDevices.map((d) => d.deviceID));
		return orgLogs.filter(
			(log) => lockerDeviceIDs.has(log.deviceID) && (!PERSON_ATTRIBUTED_ONLY || log.personID != null)
		);
	}
);

// A frozen [] so a miss is one stable reference, never a fresh array per render.
const EMPTY_LOGS = Object.freeze([]);

// ONE pass, then O(1) per lookup: each surface filtering the whole feed for itself was
// O(surfaces × logs). A null id is skipped — nothing can ever look it up.
const indexLogsBy = (logs, key) => {
	const byID = new Map();
	logs.forEach((log) => {
		const id = log[key];
		if (id == null) return;
		const bucket = byID.get(id);
		if (bucket) bucket.push(log);
		else byID.set(id, [log]);
	});
	return byID;
};

const selectLockerLogIndexByPerson = createSelector([selectLockerLogs], (logs) => indexLogsBy(logs, 'personID'));
const selectLockerLogIndexByDevice = createSelector([selectLockerLogs], (logs) => indexLogsBy(logs, 'deviceID'));

//📌 One person's locker activity, straight off the loaded window — no fetch, no hook.
// Under-reports: rows the server could not attribute carry no PersonID to join on.
export const selectLockerLogsForPerson = (state, personID) =>
	selectLockerLogIndexByPerson(state).get(personID) ?? EMPTY_LOGS;

//📌 One locker's activity. Same index, same contract.
export const selectLockerLogsForDevice = (state, deviceID) =>
	selectLockerLogIndexByDevice(state).get(deviceID) ?? EMPTY_LOGS;

//📌 "Not counted yet" vs "nothing to count" — the window no longer blocks first paint.
// Devices count too: the scope filters by LOADED lockers, so an unlanded list reads empty.
export const selectLockerLogsLoading = (state) =>
	!selectLogsWindowReady(state, LOCKER_LOG_SCOPE, LOCKER_LOG_DAYS) || !selectLockersDevicesReady(state);
